import { Layout } from '@/components/Layout';
import { PlanSection } from '@/components/PlanSection';
import { systemArchitecture } from '@/data/projectPlan';
import { IMAGES } from '@/assets/images';
import { motion } from 'framer-motion';
import { Database, Layers, Server, Shield, Zap } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export default function SystemDesign() {
  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 z-0 opacity-30">
            <img
              src={IMAGES.SMART_FACTORY_2}
              alt="시스템 설계"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
          
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-3xl mx-auto"
            >
              <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                시스템 설계
              </h1>
              <p className="text-xl text-muted-foreground">
                확장 가능하고 안정적인 3-Tier 아키텍처 기반 시스템 설계
              </p>
            </motion.div>
          </div>
        </section>

        <div className="container mx-auto px-4 py-12">
          <PlanSection title="시스템 아키텍처 개요" className="mb-12">
            <div className="prose prose-lg max-w-none mb-8">
              <p className="text-muted-foreground">
                {systemArchitecture.overview}를 채택하여 프레젠테이션, 애플리케이션, 데이터 계층을 명확히 분리합니다.
                이를 통해 유지보수성과 확장성을 극대화하고, 각 계층의 독립적인 개발 및 배포가 가능합니다.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {systemArchitecture.layers.map((layer, index) => (
                <motion.div
                  key={layer.name}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  <Card className="h-full hover:shadow-lg transition-shadow duration-200">
                    <CardHeader>
                      <div className="flex items-center gap-3 mb-2">
                        {index === 0 && <Layers className="w-6 h-6 text-primary" />}
                        {index === 1 && <Zap className="w-6 h-6 text-accent" />}
                        {index === 2 && <Database className="w-6 h-6 text-chart-3" />}
                        <CardTitle className="text-xl">{layer.name}</CardTitle>
                      </div>
                      <CardDescription>{layer.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {layer.components.map((component) => (
                          <li key={component} className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                            <span className="text-sm text-foreground">{component}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </PlanSection>

          <PlanSection title="데이터베이스 설계" className="mb-12">
            <div className="prose prose-lg max-w-none mb-8">
              <p className="text-muted-foreground">
                PostgreSQL 기반의 관계형 데이터베이스를 사용하여 데이터 무결성과 일관성을 보장합니다.
                Supabase를 통해 실시간 데이터 동기화 및 Row Level Security를 구현합니다.
              </p>
            </div>

            <div className="grid gap-6">
              {systemArchitecture.database.tables.map((table, index) => (
                <motion.div
                  key={table.name}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  <Card className="hover:shadow-lg transition-shadow duration-200">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Server className="w-5 h-5 text-primary" />
                          <CardTitle className="text-lg font-mono">{table.name}</CardTitle>
                        </div>
                        <Badge variant="secondary">{table.columns.length} 컬럼</Badge>
                      </div>
                      <CardDescription>{table.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {table.columns.map((column) => (
                          <Badge key={column} variant="outline" className="font-mono text-xs">
                            {column}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </PlanSection>

          <PlanSection title="보안 및 인증 설계" className="mb-12">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="hover:shadow-lg transition-shadow duration-200">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <Shield className="w-6 h-6 text-primary" />
                    <CardTitle>인증 시스템</CardTitle>
                  </div>
                  <CardDescription>Supabase Auth 기반 안전한 사용자 인증</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2" />
                      <div>
                        <p className="font-medium text-foreground">이메일/비밀번호 인증</p>
                        <p className="text-sm text-muted-foreground">교육기관 이메일 기반 회원가입 및 로그인</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2" />
                      <div>
                        <p className="font-medium text-foreground">세션 관리</p>
                        <p className="text-sm text-muted-foreground">JWT 토큰 기반 안전한 세션 유지</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2" />
                      <div>
                        <p className="font-medium text-foreground">비밀번호 재설정</p>
                        <p className="text-sm text-muted-foreground">이메일 인증을 통한 안전한 비밀번호 복구</p>
                      </div>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow duration-200">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <Shield className="w-6 h-6 text-accent" />
                    <CardTitle>권한 관리</CardTitle>
                  </div>
                  <CardDescription>역할 기반 접근 제어 (RBAC)</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2" />
                      <div>
                        <p className="font-medium text-foreground">교수 권한</p>
                        <p className="text-sm text-muted-foreground">전체 데이터 조회, 수정, 삭제 권한</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2" />
                      <div>
                        <p className="font-medium text-foreground">학생 권한</p>
                        <p className="text-sm text-muted-foreground">본인 팀 데이터 조회 및 제한적 수정</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2" />
                      <div>
                        <p className="font-medium text-foreground">Row Level Security</p>
                        <p className="text-sm text-muted-foreground">데이터베이스 레벨에서 권한 검증</p>
                      </div>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </PlanSection>

          <PlanSection title="UI/UX 설계 원칙">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  title: '직관적 네비게이션',
                  description: '사이드바 기반 명확한 메뉴 구조',
                  color: 'text-primary',
                },
                {
                  title: '반응형 디자인',
                  description: '데스크톱과 태블릿 최적화',
                  color: 'text-accent',
                },
                {
                  title: '데이터 시각화',
                  description: '차트와 그래프로 정보 전달',
                  color: 'text-chart-3',
                },
                {
                  title: '접근성 준수',
                  description: 'WCAG 2.1 AA 레벨 준수',
                  color: 'text-chart-4',
                },
              ].map((principle, index) => (
                <motion.div
                  key={principle.title}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  <Card className="h-full hover:shadow-lg transition-shadow duration-200">
                    <CardHeader>
                      <CardTitle className={`text-lg ${principle.color}`}>
                        {principle.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{principle.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </PlanSection>
        </div>
      </div>
    </Layout>
  );
}
